import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";

const STATUS_TONE = {
  draft: "muted",
  in_progress: "warn",
  approved: "success",
  published: "success",
  archived: "muted",
  failed: "danger",
};

export default function CreateOverview() {
  const [campaigns, setCampaigns] = useState(null);
  const [assets, setAssets] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // 2026-10-26 — Legacy "/generation/jobs" was removed with the
        // Copywriter + Guardian page. The governed campaign → Creative
        // pipeline is now the only path to new copy; this overview
        // shows campaigns in flight and asset_sets ready to publish.
        const [c, a] = await Promise.all([
          api.get("/campaigns"),
          api.get("/asset-sets/publishable").catch(() => ({ data: { asset_sets: [] } })),
        ]);
        if (!alive) return;
        setCampaigns(c.data?.campaigns || []);
        setAssets(a.data?.asset_sets || []);
      } catch (e) {
        if (alive) setErr(formatApiError(e));
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <Shell>
      <div className="bo-page" data-testid="create-overview-page">
        <header className="bo-page-header">
          <div>
            <div className="bo-eyebrow">CREATE</div>
            <h1 data-testid="create-overview-title">Campaigns & creative</h1>
            <p className="bo-page-lede">
              Campaigns in flight, assets ready to publish, and the latest generation runs — all in one place.
            </p>
          </div>
          <div className="bo-page-actions">
            <Link to="/brand-os/create/import-video" className="bo-btn" data-testid="create-import-video-cta">
              Import video
            </Link>
            <Link to="/brand-os/campaigns" className="bo-btn bo-btn-primary" data-testid="create-new-campaign-cta">
              Manage campaigns
            </Link>
          </div>
        </header>

        {err && <ErrorBanner error={err} testId="create-overview-error" />}

        <div className="bo-grid-3" data-testid="create-overview-cards">
          <section className="bo-card" data-testid="create-campaigns-card">
            <header className="bo-card-header">
              <h3>Campaigns</h3>
              <Link to="/brand-os/campaigns" className="bo-link-sm">See all →</Link>
            </header>
            {!campaigns ? (
              <LoadingCard />
            ) : campaigns.length === 0 ? (
              <EmptyState testId="create-no-campaigns" title="No campaigns yet" description="Start your first campaign to begin." />
            ) : (
              <>
                <div className="bo-metric-row">
                  <div className="bo-metric">
                    <div className="bo-metric-label">Total</div>
                    <div className="bo-metric-value">{campaigns.length}</div>
                  </div>
                </div>
                <ul className="bo-list-tight">
                  {campaigns.slice(0, 4).map((c) => (
                    <li key={c.id}>
                      <div>
                        <div className="bo-list-title">{c.name}</div>
                        {c.objective && <div className="bo-list-sub">{c.objective}</div>}
                      </div>
                      <Pill tone={STATUS_TONE[c.status] || "muted"}>{c.status || "draft"}</Pill>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>

          <section className="bo-card" data-testid="create-assets-card">
            <header className="bo-card-header">
              <h3>Publishable assets</h3>
              <Link to="/brand-os/library" className="bo-link-sm">Library →</Link>
            </header>
            {!assets ? (
              <LoadingCard />
            ) : assets.length === 0 ? (
              <EmptyState testId="create-no-assets" title="Nothing ready to publish" description="Approve an asset set to see it here." />
            ) : (
              <>
                <div className="bo-metric-row">
                  <div className="bo-metric">
                    <div className="bo-metric-label">Approved</div>
                    <div className="bo-metric-value">{assets.length}</div>
                  </div>
                </div>
                <ul className="bo-list-tight">
                  {assets.slice(0, 4).map((a) => (
                    <li key={a.id}>
                      <span className="bo-list-title">{a.name || a.id?.slice(0, 8)}</span>
                      <Link to="/brand-os/publish/new" className="bo-link-sm">Publish</Link>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>
        </div>
      </div>
    </Shell>
  );
}
